import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Routes, Route, Navigate } from 'react-router-dom';
import PurchaseProductList from './PurchaseForm';
import ProductList from '../../Product/ProductList';
import ProductForm from '../../Product/ProductForm';
import SoldProductForm from '../../Product/SoldProductForm';
import { fetchPurchases } from './PurchaseService';
import { API_CONFIG } from '../../../utils/apiConfig';
import './PurchasingAdminPanel.css';
import InventoryList from './inventoryList';
import PurchaseReceiptDetails from './PurchaseRescipt';
import AddPriceList from './AddPriceList.js';
import PriceListView from './PriceListView.js';
import PurchaseReceiptList from './PurchaseReceiptList.js';
import ProductReturns from './ProductReturns.js';
import SalesMigration from './SalesMigration.js';
import QuickMigration from './QuickMigration.js';
import DirectMigration from './DirectMigration.js';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';

const PurchasingAdminPanel = () => {
  const [user, setUser] = useState(null);
  const [message, setMessage] = useState(null);
  const [showPriceMenu, setShowPriceMenu] = useState(false);
  const [showInventoryMenu, setShowInventoryMenu] = useState(false);
  const [showProductsMenu, setShowProductsMenu] = useState(false);
  const [showPurchaseMenu, setShowPurchaseMenu] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const API_BASE_URL = API_CONFIG.BASE_URL;
  const getAuthToken = useCallback(async () => {
    if (!currentUser) {
      throw new Error('No authenticated user');
    }
    return await currentUser.getIdToken(true);
  }, [currentUser]);
  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) return;
      try {
        const token = await getAuthToken();
        const headers = { Authorization: `Bearer ${token}` };
        if (!userProfile) {
          throw new Error('User profile not found.');
        }
        const userRole = userProfile?.role?.toLowerCase() || localStorage.getItem('role')?.toLowerCase();
        const userDept = userProfile?.department?.toLowerCase() || localStorage.getItem('dept')?.toLowerCase();
        if (!['super_admin', 'superadmin', 'purchasing_admin', 'purchase_admin', 'purchaseadmin'].includes(userRole) &&
            !(userRole === 'admin' && ['purchase', 'purchasing'].includes(userDept))) {
          throw new Error('Insufficient permissions.');
        } 
        setUser(userProfile);
        try {
          await axios.get(`${API_BASE_URL}/health`, { headers });
        } catch (healthError) {
        }

        try {
          await fetchPurchases(token);
        } catch (purchaseError) {
          if (purchaseError.response?.status === 404 || purchaseError.message?.includes('404') || purchaseError.message?.includes('Not Found')) {
            setMessage({ type: 'info', text: 'Purchase endpoint not available on server. Purchase features may be limited.' });
          } else {
            setMessage({ type: 'warning', text: 'Purchase data unavailable. Some features may be limited.' });
          }
          // Don't block the entire panel for purchase error
        }

      } catch (err) {
        setMessage({ type: 'error', text: err.message || 'Failed to fetch data' });
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [navigate, currentUser, userProfile, getAuthToken, API_BASE_URL]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown')) {
        setShowProductsMenu(false);
        setShowPurchaseMenu(false);
        setShowInventoryMenu(false);
        setShowPriceMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('darkMode');
    localStorage.removeItem('role');
    localStorage.removeItem('dept');
    localStorage.removeItem('email');
    localStorage.removeItem('userId');
    navigate('/login');
  };


  const handleNavigation = (path) => {
    setIsMobileMenuOpen(false);
    setShowProductsMenu(false);
    setShowPurchaseMenu(false);
    setShowInventoryMenu(false);
    setShowPriceMenu(false);
    navigate(`/purchasing-admin/${path}`);
  };

  // Show loading while fetching user data
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '1.2rem',
        color: '#666'
      }}>
        Loading Purchasing Admin Panel...
      </div>
    );
  }

  // Check for either user state or userProfile from auth context
  if (!user && !userProfile) {
    return <Navigate to="/login" replace />;
  }
  

  const handleCancelProductForm = () => {
    navigate('/purchasing-admin/products/list');
  };

  return (
    <div className='purchasing-admin-panel'>
      <header className="panel-header">
        <div className="header-content">
          <h1>Purchasing Admin Panel</h1>
          <button 
            className="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle mobile menu"
          >
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </button>
        </div>
        <div className="user-info">
          <button className="btn-4d" onClick={handleLogout}>Logout</button>
        </div>
      </header>

      <nav className={`panel-nav ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
        <ul>
          <li>
            <div className="dropdown">
              <button className="btn-3d" onClick={() => setShowProductsMenu(!showProductsMenu)}>Products</button>
              {showProductsMenu && (
                <div className="dropdown-menu">
                  <button onClick={() => {
                    setShowProductsMenu(false);
                    handleNavigation('products/add');
                  }}>Add Product</button>
                  <button onClick={() => {
                    setShowProductsMenu(false);
                    handleNavigation('products/list');
                  }}>Product List</button>
                  <button onClick={() => {
                    setShowProductsMenu(false);
                    handleNavigation('products/sold');
                  }}>Sold Product</button>
                  <button onClick={() => {
                    setShowProductsMenu(false);
                    handleNavigation('products/returns');
                  }}>Product Returns</button>
                </div>
              )}
            </div>
          </li>
          <li>
            <div className="dropdown">
              <button className="btn-3d" onClick={() => setShowPurchaseMenu(!showPurchaseMenu)}>Purchase</button>
              {showPurchaseMenu && (
                <div className="dropdown-menu">
                  <button onClick={() => {
                    setShowPurchaseMenu(false);
                    handleNavigation('purchases/product');
                  }}>Purchase Product List</button>
                  <button onClick={() => {
                    setShowPurchaseMenu(false);
                    handleNavigation('purchases/receipt');
                  }}>Purchase Receipt</button>
                  <button onClick={() => {
                    setShowPurchaseMenu(false);
                    handleNavigation('purchases/receipt-List');
                  }}>Purchase Receipt List</button>
                </div>
              )}
            </div>
          </li>
          <li>
            <div className="dropdown">
              <button className="btn-3d" onClick={() => setShowInventoryMenu(!showInventoryMenu)}>Inventory</button>
              {showInventoryMenu && (
                <div className="dropdown-menu">
                  <button onClick={() => {
                    setShowInventoryMenu(false);
                    handleNavigation('inventory/list');
                  }}>Inventory List</button>
                </div>
              )}
            </div>
          </li>
          <li>
            <div className="dropdown">
              <button className="btn-3d" onClick={() => setShowPriceMenu(!showPriceMenu)}>Price List</button>
              {showPriceMenu && (
                <div className="dropdown-menu">
                  <button onClick={() => {
                    setShowPriceMenu(false);
                    handleNavigation('price-list/add');
                  }}>Add Price List</button>
                  <button onClick={() => {
                    setShowPriceMenu(false);
                    handleNavigation('price-list/view');
                  }}>View Price Lists</button>
                </div>
              )}
            </div>
          </li>
        </ul>
      </nav>

      <main className="panel-content">
        {message && <div className={`message ${message.type}`} role="alert">{message.text}</div>}
        <Routes>
          <Route index element={
            <div>
              <h2 className="page-heading">🏠 Purchasing Admin Dashboard</h2>
              <div className="dashboard-welcome">
                <p>Welcome to the Purchasing Admin Panel. Use the navigation menu to access different features.</p>
                <div className="quick-actions">
                  <button onClick={() => navigate('/purchasing-admin/products/add')} className="btn-3d">Add Product</button>
                  <button onClick={() => navigate('/purchasing-admin/products/list')} className="btn-3d">View Products</button>
                  <button onClick={() => navigate('/purchasing-admin/products/sold')} className="btn-3d">Sold Products</button>
                </div>
              </div>
            </div>
          } />
          <Route path="products/add" element={
            <div>
              <h2 className="page-heading">📦 Add New Product</h2>
              <ProductForm onCancel={handleCancelProductForm} onSubmitSuccess={() => navigate('/purchasing-admin/products/list')} />
            </div>
          } />
          <Route path="products/list" element={
            <div>
              <h2 className="page-heading">📋 Product List</h2>
              <ProductList />
            </div>
          } />
          <Route path="products/sold" element={
            <div>
              <h2 className="page-heading">💰 Sold Products</h2>
              <SoldProductForm />
            </div>
          } />
          <Route path="products/returns" element={
            <div>
              <h2 className="page-heading">🔄 Product Returns</h2>
              <ProductReturns />
            </div>
          } />
          <Route path="purchases/product" element={
            <div>
              <h2 className="page-heading">🛒 Purchase Product List</h2>
              <PurchaseProductList />
            </div>
          } />
          <Route path="purchases/receipt" element={
            <div>
              <h2 className="page-heading">🧾 Purchase Receipt</h2>
              <PurchaseReceiptDetails onSubmit={() => {}} onCancel={() => navigate(-1)} />
            </div>
          } />
          <Route path='purchases/receipt-List' element={
            <div>
              <h2 className="page-heading">📄 Purchase Receipt List</h2>
              <PurchaseReceiptList/>
            </div>
          }/>
          <Route path="inventory/list" element={
            <div>
              <h2 className="page-heading">📦 Inventory Management</h2>
              <InventoryList />
            </div>
          } />
          <Route path="price-list/add" element={
            <div>
              <h2 className="page-heading">💲 Add Price List</h2>
              <AddPriceList/>
            </div>
          } />
          <Route path="price-list/view" element={
            <div>
              <h2 className="page-heading">📊 Price Lists</h2>
              <PriceListView />
            </div>
          } />
          <Route path="sales-migration" element={
            <div>
              <h2 className="page-heading">🔄 Sales Data Migration</h2>
              <SalesMigration />
            </div>
          } />
          <Route path="quick-migration" element={
            <div>
              <h2 className="page-heading">🚀 Quick Migration</h2>
              <QuickMigration />
            </div>
          } />
          <Route path="direct-migration" element={
            <div>
              <h2 className="page-heading">💾 Direct Migration</h2>
              <DirectMigration />
            </div>
          } />
          <Route path="*" element={
            <div>
              <h2 className="page-heading">404 - Page Not Found</h2>
              <p>The requested page could not be found.</p>
              <button onClick={() => navigate('/purchasing-admin')} className="btn-3d">Back to Dashboard</button>
            </div>
          } />
        </Routes>
      </main>
    </div>
  );
};

export default PurchasingAdminPanel;
